import { Alert, Stack, TextField, type Theme, Typography } from '@mui/material';
import type { SystemStyleObject } from '@mui/system';
import { DROPDOWN_CHECK_SIZE } from '@onevision-resources/design-theme';
import { useId, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { FormFieldLabel } from 'src/components/Forms/FormFieldLabel/form-field-label';
import { SelectMenu, type SelectMenuOption } from 'src/components/SelectMenu';
import type { LegalDocumentFormValues } from 'src/pages/Settings/memberships/components/legal-document/types';
import { panelFieldLabelSx } from 'src/pages/Settings/memberships/styles';

const VERSION_SLOT_INSET = '0.5rem';

const VERSION_SLOT_SIZE = '1.75rem';

// The check and the delete button share one fixed slot, centered on the whole row. A row never has
// both: the checked version is the segment's own, so it's in use and can't be deleted.
const versionMenuSx: SystemStyleObject<Theme> = {
  '& [cmdk-list] [cmdk-item]': { position: 'relative', paddingRight: '2.75rem' },
  '& [cmdk-item] [data-check], & [cmdk-item] [data-version-delete]': {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    margin: 0,
    padding: 0,
  },
  '& [cmdk-item] [data-version-delete]': {
    right: VERSION_SLOT_INSET,
    width: VERSION_SLOT_SIZE,
    height: VERSION_SLOT_SIZE,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  // The check is a mask drawn at its own size, so it is centred in the slot rather than stretched.
  '& [cmdk-item] [data-check]': {
    right: `calc(${VERSION_SLOT_INSET} + (${VERSION_SLOT_SIZE} - ${DROPDOWN_CHECK_SIZE}) / 2)`,
  },
  '& [data-version-description]': {
    display: 'block',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
};

interface LegalDocumentFieldsProps {
  versionOptions: SelectMenuOption[];
  selectedVersionId: string;
  isNewVersion: boolean;
  onVersionChange: (documentId: string) => void;
  onNewVersion: () => void;
  sharingNote?: string;
  domainWarning?: string;
}

export const LegalDocumentFields = ({
  versionOptions,
  selectedVersionId,
  isNewVersion,
  onVersionChange,
  onNewVersion,
  sharingNote,
  domainWarning,
}: LegalDocumentFieldsProps) => {
  const { control } = useFormContext<LegalDocumentFormValues>();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const nameId = useId();

  return (
    <Stack p={2} gap={3}>
      <Stack spacing={1}>
        <Typography variant="caption" sx={panelFieldLabelSx}>
          Version
        </Typography>
        <SelectMenu
          label="Version"
          options={versionOptions}
          value={selectedVersionId}
          onChange={onVersionChange}
          triggerLabel={isNewVersion ? 'New version (not saved)' : undefined}
          open={isMenuOpen}
          onOpenChange={setIsMenuOpen}
          createAction={{
            label: 'New version',
            onSelect: () => {
              setIsMenuOpen(false);
              onNewVersion();
            },
          }}
          menuSx={versionMenuSx}
          menuWidth="trigger"
          sx={{ width: '100%' }}
        />
        {sharingNote ? (
          <Typography variant="caption" color="text.secondary">
            {sharingNote}
          </Typography>
        ) : null}
      </Stack>

      <Controller
        name="name"
        control={control}
        rules={{ required: 'Enter a version name.' }}
        render={({ field, fieldState }) => (
          <Stack>
            <FormFieldLabel htmlFor={nameId}>Version name</FormFieldLabel>
            <TextField
              {...field}
              id={nameId}
              size="small"
              error={Boolean(fieldState.error)}
              helperText={
                fieldState.error?.message ??
                'Only your team sees this, to tell versions apart. Clients see the title on the page.'
              }
            />
          </Stack>
        )}
      />

      {domainWarning ? <Alert severity="warning">{domainWarning}</Alert> : null}
    </Stack>
  );
};
